import { format, isToday, isValid } from "date-fns";

import {
  GetReportReviewersQuery,
  GetReviewerCandidatesQuery,
  ReportReviewerStatus,
  RolesEnum,
} from "@api/graphql/types-and-hooks";
import { getReviewUserDisplay } from "@components/QaReview/components/commentAuthor";
import { getRoleLabelFromEnum } from "@pages/Members/utils/providerRoles";

export type Reviewer = GetReportReviewersQuery["getReportReviewers"][number];

type StatusEntry = Reviewer["statusLog"][number];

export const latestRequest = (reviewers: Reviewer[]): StatusEntry | undefined =>
  reviewers
    .flatMap(({ reviewer, statusLog }) => statusLog.map((entry) => ({ ...entry, reviewerId: reviewer?.id })))
    .filter(({ status, actor, reviewerId }) => status === ReportReviewerStatus.Requested && actor?.id !== reviewerId)
    .sort((a, b) => b.createdDate.localeCompare(a.createdDate))[0];

export type ReviewerPerson = { id: string; name: string; initials: string; role?: string };

export const REVIEWER_ROLES = [
  RolesEnum.Admin,
  RolesEnum.AssessmentBcba,
  RolesEnum.Bcba,
  RolesEnum.EvaluationPsychologist,
  RolesEnum.Psychologist,
];

export const INCLUDE_INTERNAL_REVIEWERS = !["production", "prodtest"].includes(import.meta.env.MODE);

export const REVIEWER_STATUS_LABELS: Record<ReportReviewerStatus, string> = {
  [ReportReviewerStatus.Requested]: "Requested",
  [ReportReviewerStatus.Approved]: "Approved",
  [ReportReviewerStatus.ChangesRequested]: "Changes requested",
  [ReportReviewerStatus.Withdrawn]: "Withdrawn",
};

export const REVIEWER_STATUS_BADGE_CLASSES: Record<ReportReviewerStatus, string> = {
  [ReportReviewerStatus.Requested]: "",
  [ReportReviewerStatus.Approved]: "bg-green-100 text-green-800",
  [ReportReviewerStatus.ChangesRequested]: "bg-amber-100 text-amber-800",
  [ReportReviewerStatus.Withdrawn]: "",
};

export const formatReviewTime = (value: string): string => {
  const date = new Date(value);
  if (!isValid(date)) return "";
  return `${isToday(date) ? "today" : format(date, "MMM d")}, ${format(date, "h:mm a")}`;
};

export const toReviewerPerson = (
  user: { id: string; firstName: string; lastName?: string | null; email?: string | null },
  role?: string,
): ReviewerPerson => ({ id: user.id, role, ...getReviewUserDisplay(user) });

export const toCandidates = (data: GetReviewerCandidatesQuery | undefined): ReviewerPerson[] =>
  (data?.getOrganizationProviders?.edges ?? []).flatMap((edge) => {
    const user = edge?.node?.auth0User;
    const metadata = edge?.node?.providermetadata;
    if (!user || !metadata) return [];
    const roles = (edge?.node?.organizationuserrolesSet?.edges ?? []).flatMap((roleEdge) =>
      roleEdge?.node ? [getRoleLabelFromEnum(roleEdge.node.role)] : [],
    );
    return [toReviewerPerson({ ...metadata, id: user.id }, roles.join(", ") || undefined)];
  });

type StatusLogEntry = Reviewer["statusLog"][number];

// The log is oldest first; the lib target has no findLast.
const newestFirst = (reviewer: Reviewer): StatusLogEntry[] => [...reviewer.statusLog].reverse();

/** When the reviewer last reached a status, e.g. their latest send-back or the latest time they were asked. */
export const latestStatusEntry = (reviewer: Reviewer, status: ReportReviewerStatus) =>
  newestFirst(reviewer).find((entry) => entry.status === status);

/** The reviewer's most recent send-back. */
export const latestSendBack = (reviewer: Reviewer) =>
  latestStatusEntry(reviewer, ReportReviewerStatus.ChangesRequested);

/** Every note the reviewer left, on any action, newest first. */
export const reviewerNotes = (reviewer: Reviewer): StatusLogEntry[] =>
  newestFirst(reviewer).filter((entry) => entry.note.trim());
